import { Select, Stack, Textarea, TextInput } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type { PatientFormData } from "@benchmark/core/types";
import { FormSection, FormGroup } from "@benchmark/ui/components/atomic";
import { localCalendarDateTodayISO } from "@benchmark/ui/utils";

const answers = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
const asSelectValue = (value: boolean | undefined) =>
  value === undefined ? null : value ? "yes" : "no";

/** Optional fax evidence in the Member step, using the existing consent fields. @agentic connections */
export function PCPReferralSection({
  form,
}: {
  form: UseFormReturnType<PatientFormData>;
}) {
  return (
    <FormSection
      title="PCP Referral"
      subtitle="Optional information from the referral fax"
    >
      <Stack gap="md">
        <FormGroup>
          <Select
            label="PCP recommendation"
            placeholder="Not recorded"
            clearable
            data={[
              { value: "refer-in", label: "Refer in" },
              { value: "refer-out", label: "Refer out" },
              { value: "defer-to-advocate", label: "Defer to advocate" },
            ]}
            value={form.values["pcp-recommendation"] ?? null}
            onChange={(value) =>
              form.setFieldValue(
                "pcp-recommendation",
                (value || undefined) as PatientFormData["pcp-recommendation"],
              )
            }
            error={form.errors["pcp-recommendation"]}
          />
          <Select
            label="PCP-reported high risk"
            placeholder="Not recorded"
            clearable
            data={answers}
            value={asSelectValue(form.values["pcp-high-risk"])}
            onChange={(value) =>
              form.setFieldValue(
                "pcp-high-risk",
                value === null ? undefined : value === "yes",
              )
            }
          />
        </FormGroup>
        <FormGroup>
          <Select
            label="CoCM consent"
            placeholder="Not recorded"
            clearable
            data={answers}
            value={asSelectValue(form.values["cocm-consent-verified"])}
            onChange={(value) => {
              form.setFieldValue(
                "cocm-consent-verified",
                value === null ? undefined : value === "yes",
              );
              if (value !== "yes") {
                form.setFieldValue("cocm-consent-date", "");
                form.clearFieldError("cocm-consent-date");
              }
            }}
          />
          {form.values["cocm-consent-verified"] === true && (
            <TextInput
              label="CoCM consent date"
              type="date"
              required
              max={localCalendarDateTodayISO()}
              {...form.getInputProps("cocm-consent-date")}
            />
          )}
        </FormGroup>
        <Textarea
          label="Additional PCP recommendations"
          description="Up to 4,000 characters"
          autosize
          minRows={3}
          {...form.getInputProps("pcp-additional-recommendations")}
        />
      </Stack>
    </FormSection>
  );
}
